import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AutoComplete, SelectPicker } from "rsuite"
import { useTranslation } from "react-i18next"
import { doQuery } from "../../utils/useQuery"
import UserModel, { TUser } from "@client/models/user"
import ProjectModel, { TProject } from "@client/models/project"

export interface UserProject {
    _id: string
    user: TUser
    project: TProject
    permissions: {
        read: boolean
        write: boolean
        delete: boolean
    },
    label?: string,
}

export enum RelationType {
    User = 'user',
    Project = 'project',
}

export type RelationNameProps = {
    type: RelationType,
    relation: UserProject,
    setInputValue: any
}

const userModel = new UserModel()
const projectModel = new ProjectModel()

export default function RelationName({ type, relation, setInputValue }: RelationNameProps) {
    const { t } = useTranslation()
    const [page, setPage] = useState<number>(1)
    const [searchText, setSearchText] = useState(() => '')
    const [searchResult, setSearchResult] = useState([])
    const [selectedRelation, setSelectedRelation] = useState<string | TUser | TProject>(
        (relation.label ? relation.label : (type == RelationType.Project ? relation.project : relation.user)) || '')
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

    const searchRelation = useCallback(async (search: string) => {
        let model
        if (type == RelationType.Project) model = projectModel
        else if (type == RelationType.User) model = userModel
        else return

        //if (search.length < 3) return

        const res: any = await doQuery(model.list, { page, count: 20, q: search })
        setSearchResult(res.data?.rows || [])
    }, [type])

    // const saveRelation = useCallback(async () => {
    // }, [selectedRelation])

    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current)
        }

        if (searchText) {
            debounceTimeout.current = setTimeout(() => {
                searchRelation(searchText)
            }, 300)
        } else {
            //setSearchResult([])
        }
    }, [searchText, searchRelation])

    const pickerData = useMemo(() => {
        return searchResult.map((p: any) => {
            if (type === RelationType.Project) return { label: p.name, value: p._id }
            if (type === RelationType.User) return { label: `${p.lastName}, ${p.firstName}`, value: p._id }
            return { label: p._id, value: p._id }
        })
    }, [searchResult, type])

    let placeholder: string = ''
    if (type == RelationType.User) {
        placeholder = t('Select user ...')
    } else if (type == RelationType.Project) {
        placeholder = t('Select project ...')
    } else {
        return t('No assigned type found.')
    }

    const getRelationLabel = (selectedRelation: string | TProject | TUser): string => {
        const id = typeof selectedRelation == 'string' ? selectedRelation : selectedRelation._id
        const selected = pickerData.find(item => item.value == id)
        if (selected?.label) return selected.label
        if (type == RelationType.Project) return (selectedRelation as TProject).name
        if (type == RelationType.User) return (selectedRelation as TUser).lastName + ', ' + (selectedRelation as TUser).firstName
        return selectedRelation.toString()
    }

    const onChange = (id: string | null) => {
        if (!id) return

        const item: any = searchResult.find((r: any) => r._id == id)
        setSelectedRelation(item)
        const newInputValue = { ...relation }
        if (type == RelationType.Project) {
            newInputValue.project = item as TProject
        }
        if (type == RelationType.User) {
            newInputValue.user = item as TUser
        }
        setInputValue(newInputValue, getRelationLabel(item))
    }

    const renderMenu = (menu: any) => {
        if (searchText.length === 0) {
            return (
                <p style={{ padding: 4, color: '#999' }}>
                    {type == RelationType.Project && t('Enter a name to find project.')}
                    {type == RelationType.User && t('Enter a name to find user.')}
                </p>
            )
        }
        if (menu.props.className == 'rs-picker-none') {
            return (
                <p style={{ padding: 4, color: '#999' }}>
                    {t('Loading')}
                </p>
            )
        }

        return menu
    }

    if (selectedRelation) {
        return <span className={`relation-name relation-name-${type}`}>{getRelationLabel(selectedRelation)}</span>
    }

    return <AutoComplete
        key={type}
        className={`relation-name relation-name-${type}`}
        style={{ width: 250 }}
        placeholder={placeholder}
        appearance="subtle"
        data={pickerData}
        value={searchText}
        //value={typeof selectedRelation == 'string' ? selectedRelation : (selectedRelation as any)._id}
        onChange={setSearchText}
        onSelect={onChange}
    //renderMenu={renderMenu}
    //cleanable
    />

    return <SelectPicker
        key={type}
        className={`relation-name relation-name-${type}`}
        placeholder={placeholder}
        style={{ width: 250 }}
        appearance="subtle"
        data={pickerData}
        defaultValue={typeof selectedRelation == 'string' ? selectedRelation : (selectedRelation as any)._id}
        onSearch={setSearchText}
        onChange={onChange}
        renderMenu={renderMenu}
        labelKey="label" // explizit gesetzt
        valueKey="value" // explizit gesetzt
        searchable
        virtualized
        cleanable
    />
}